/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getCurrentEvents,
  getLabOrderPhases,
  getLatestOrderEvents,
} from '../../../lms/utils'
import {
  CLabel,
  COLLECTED,
  COLLECTION_APPROVED,
  COLLECTION_REJECTED,
  LabResult,
  LmsPanel,
  LOC_ALLOCATED,
  toastOptions,
} from '../../models'
import { useSingleLabOrder } from '../useSingleLabOrder'
import Select from 'react-select'
import KeycloakService from '../../../../utils/keycloakService'
import { getAgeInYears, readableDateFormat } from '../../../../utils/dateUtils'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { setLmsActiveTab, setLmsDateFilter } from '../../lmsSlice'
import LabelLogo from '../../../../assets/images/sampleLabelPrint.webp'
import { toast } from 'react-toastify'
import { COLLECTION_WRITE, TESTING_WRITE } from '../../../../utils/roles'
import {
  AcknowledgeBy,
  CREATE_LAB_RESULT,
  ERROR_CREATE_EVENT,
  ERROR_LAB_RESULT,
  ERROR_UPDATE_ORDER,
  GENERIC_ERROR,
  LabOrder,
  LabTest,
  LmstableHeading,
  OrderEvent,
  OrderEventTest,
  Panel,
  SAMPLE_APPROVED_MSG,
  Tryagain,
  UPDATE_LAB_ORDERS_SHORT,
} from '../../../labTests/models'
import { startSxpProxy } from '../../../../utils/api'
import { LABS_PROJECT_ID } from '../../../../utils/constants'
import ToastMessage from '../ToastMessage'
import { getSelectedTestSet, makeName } from '../../utils'
import LoadingButton from '../../../../components/LoadingButton'
import LocationByPrefix from '../../../patients/dashboard/LocationByPrefix'
import { sendSocketData } from '../../../Notification/Socket'
import { NEW_IPD_DATA_UPDATE } from '../../../Notification/Constants'
import {
  allLabTab,
  collectOrderHeading,
  DashboardTabs,
  lmsIntent,
  locErrorMsg,
  noLrGeneration,
  orderCollectedState,
} from '../../constants'
import {
  selectLocations,
  selectSelectedLocationId,
} from '../../../location/locationSlice'
import {
  generateGlobalId,
  updatedOrderEvent,
  updateLabOrderPU,
  updateOrderPhases,
} from '../../../../services/LmsService'
import { printLabelPDF } from '../dashboard/PrintLabelPDF'
const CollectOrder = () => {
  const { id } = useParams()
  const [reload, setReload] = useState<number>(1)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { order, patient } = useSingleLabOrder(id ?? '', reload)
  const lastEvent = order?.order_events?.[0]
  const eventId = order?.order_events?.[0]?.id
  const [checked, setChecked] = useState<number[]>([])
  const [checkAll, setCheckAll] = useState(false)
  const [checkRejected, setCheckRejected] = useState<number[]>([])
  const [checkRejectAll, setCheckRejectAll] = useState(false)
  const selectAllRef = useRef<HTMLInputElement | null>(null)
  const rejectAllRef = useRef<HTMLInputElement | null>(null)
  const [comments, setComments] = useState('')
  const [apiLoading, setApiLoading] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [selectPU, setSelectPU] = useState<{ [testId: string]: any }>({})
  const [showActions, setShowActions] = useState(false)
  const location = useAppSelector(selectSelectedLocationId)
  const uhid = patient?.identifier?.find(
    (e: any) => e.system === 'patient-identifier'
  )?.value
  useEffect(() => {
    const unsubscribe = toast.onChange(({ data, status }) => {
      if (status === 'removed' && (data as any).completed) {
        dispatch(setLmsActiveTab('testing'))
        navigate('/lms/dashboard')
      }
    })
    return () => unsubscribe()
  }, [dispatch, navigate])

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = checkAll && checked.length > 0
    }
    if (rejectAllRef.current) {
      rejectAllRef.current.indeterminate =
        checkRejectAll && checkRejected.length > 0
    }
  }, [checkAll, checked, checkRejectAll, checkRejected])

  const panels: LmsPanel[] = useMemo(() => {
    if (!order) {
      return []
    }
    const tests = order?.lab_tests ?? []
    const grouped: Record<number, LabTest[]> = tests.reduce(
      (acc: Record<number, LabTest[]>, cur) => {
        const pnlId = cur.panelId
        if (acc[pnlId]) {
          acc[pnlId].push(cur)
        } else {
          acc[pnlId] = [cur]
        }
        return acc
      },
      {}
    )
    const pnls = []
    for (const key in grouped) {
      const first = grouped[key][0]
      const pnl: Panel = {
        id: first.panelId,
        name: first.panelName,
        active: true,
        sampleId: first.sampleId,
        sampleName: first.sampleName,
        lab_tests: grouped[key],
      }
      pnls.push(pnl)
    }
    const eventTests: Record<number, OrderEventTest> =
      lastEvent?.tests?.reduce(
        (acc, cur) => ({
          ...acc,
          [cur.test_id]: cur,
        }),
        {}
      ) ?? {}
    const merged = [...(order?.panels ?? []), ...pnls]?.map((m) => {
      const first = m?.lab_tests[0]
      const obj: Partial<LmsPanel> = { ...m }
      const curTest = eventTests[first?.id]
      const validStatuses = [
        'ORDERED',
        'ORDER_MODIFIED',
        'COLLECTION_REJECTED',
        'REJECTED',
      ]
      if (curTest) {
        obj.disabled = !validStatuses.includes(curTest?.status)
        obj.checked = !validStatuses.includes(curTest?.status)
        obj.collectedBy = curTest?.updatedBy
        obj.collectedOn = curTest?.updatedOn
      }
      return obj as LmsPanel
    })
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    merged?.sort((a, b) => (a.sampleId! < b.sampleId! ? -1 : 1))
    return merged
  }, [order, lastEvent])

  const remaining = panels?.filter((p) => !p?.checked)?.length

  const isComplete =
    (checkAll && !checked?.length) ||
    (!checkAll && checked?.length === remaining)

  const handleCheck = (id?: number) => {
    if (id) {
      const available = checked.find((ch) => ch === id)
      if (available) {
        setChecked((ch) => ch.filter((ch) => ch !== id))
      } else {
        setChecked((ch) => [...ch, id])
      }
    }
  }

  const handleRejectCheck = (id?: number) => {
    if (id) {
      const available = checkRejected.find((ch) => ch === id)
      if (available) {
        setCheckRejected((ch) => ch.filter((ch) => ch !== id))
      } else {
        setCheckRejected((ch) => [...ch, id])
      }
    }
  }

  const toggleCheckAll = () => {
    setCheckAll((c) => !c)
    setChecked([])
  }

  const toggleCheckRejectAll = () => {
    setCheckRejectAll((c) => !c)
    setCheckRejected([])
  }

  const handleCollect = useCallback(async () => {
    setApiLoading(true)
    const testSet = getSelectedTestSet(panels, checkAll, checked, true)
    const pastTests = lastEvent?.tests ?? []
    const current = new Date().toISOString()
    const user = KeycloakService.getUsername()
    const currentEventTests: OrderEventTest[] = [...pastTests]
    const latestOrderEvents = getLatestOrderEvents(pastTests)
    const stateObj: Partial<OrderEvent> = {
      id: eventId,
      updated_by: user,
      updated_at: current,
      tests: getCurrentEvents(
        latestOrderEvents,
        testSet,
        currentEventTests,
        COLLECTED
      ),
    }
    updatedOrderEvent(stateObj)?.then(() => {
      const intent = lmsIntent?.updateLabOrdersShort
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { order_events, ...rest } = order as LabOrder
      const updated = {
        ...rest,
        collection_completed_on: new Date().toISOString(),
        collection_completed_by_name: KeycloakService?.getUsername(),
        collection_completed_by_id: KeycloakService?.getSub(),
        active_phases: getLabOrderPhases(currentEventTests),
        updated_on: new Date().toISOString(),
      }
      const state = {
        order: updated,
        labOrderId: id,
      }
      startSxpProxy(LABS_PROJECT_ID, intent, state).then(() => {
        sendSocketData({
          messageType: NEW_IPD_DATA_UPDATE,
          patientID: uhid,
          patientLocation: '',
          message: '',
        })
        setChecked([])
        setCheckAll(false)
        setCheckRejected([])
        setCheckRejectAll(false)
        setReload((r) => r + 1)
        setApiLoading(false)
        toast(
          <ToastMessage
            message={
              isComplete ? 'Sample Collection Complete' : 'Samples Collected'
            }
          />,
          {
            ...toastOptions,
            type: 'success',
          }
        )
        if (isComplete) {
          setTimeout(() => {
            dispatch(setLmsDateFilter(new Date().toISOString().slice(0, 10)))
          }, 1000)
        }
      })
    })
  }, [
    checkAll,
    checked,
    lastEvent,
    eventId,
    order,
    id,
    uhid,
    dispatch,
    isComplete,
    panels,
  ])

  const collectedTests = useMemo(() => {
    const eventTests: Record<number, OrderEventTest> =
      lastEvent?.tests?.reduce(
        (acc, cur) => ({
          ...acc,
          [cur.test_id]: cur,
        }),
        {}
      ) ?? {}
    return panels?.filter((p) => {
      const testId = p?.lab_tests?.[0]?.id
      return testId && eventTests?.[testId]?.status === allLabTab?.LOC_ALLOCATED
    })
  }, [panels, lastEvent])

  const handleApprove = useCallback(async () => {
    if (
      (checkRejectAll && checkRejected.length === collectedTests.length) ||
      (!checkRejectAll && checkRejected.length === 0)
    ) {
      alert('Please select samples to approve')
      return
    }
    setApiLoading(true)
    let globalId: any
    if (order?.lr_number) {
      globalId = order?.lr_number
    } else {
      const resp = await generateGlobalId()
      if (!resp?.response?.id || resp?.response?.id === '') {
        toast?.error(noLrGeneration)
        setApiLoading(false)
        return
      } else {
        globalId = Number(BigInt(resp?.response?.id))
      }
    }

    const testSet = getSelectedTestSet(
      collectedTests,
      checkRejectAll,
      checkRejected
    )
    const pastTests = lastEvent?.tests ?? []
    const current = new Date().toISOString()
    const user = KeycloakService.getUsername()
    const currentEventTests: OrderEventTest[] = [...pastTests]
    const latestOrderEvents = getLatestOrderEvents(pastTests)
    const stateObj: Partial<OrderEvent> = {
      id: eventId,
      updated_by: user,
      updated_at: current,
      tests: getCurrentEvents(
        latestOrderEvents,
        testSet,
        currentEventTests,
        COLLECTION_APPROVED,
        current
      ),
      comments: comments ?? undefined,
    }
    await updatedOrderEvent(stateObj)?.then(async () => {
      const resultObj: Partial<LabResult> = {
        lab_order_id: id ? parseInt(id) : 0,
        patient_id: patient?.id,
      }
      const results = currentEventTests
        ?.filter(
          (pt) =>
            pt?.status === COLLECTION_APPROVED && pt?.updatedOn === current
        )
        ?.map((pt) => ({
          ...resultObj,
          collected_on: pt?.updatedOn,
          collected_by: pt?.updatedBy,
          lab_test_id: pt?.test_id,
        }))

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { order_events, ...rest } = order as LabOrder
      const updated = {
        ...rest,
        acknowledgement_completed_on: new Date().toISOString(),
        acknowledgement_completed_by_name: KeycloakService?.getUsername(),
        acknowledgement_completed_by_id: KeycloakService?.getSub(),
        status: orderCollectedState,
        lr_number: rest?.lr_number ?? globalId,
        active_phases: getLabOrderPhases(currentEventTests),
        updated_on: new Date().toISOString(),
      }
      const state = {
        order: updated,
        labOrderId: id,
      }
      startSxpProxy(LABS_PROJECT_ID, CREATE_LAB_RESULT, {
        labResults: results,
      })
        ?.then((labResultResp) => {
          if (!labResultResp) {
            throw new Error(ERROR_LAB_RESULT)
          }

          return startSxpProxy(LABS_PROJECT_ID, UPDATE_LAB_ORDERS_SHORT, state)
        })
        ?.then((updateResp) => {
          if (!updateResp) {
            throw new Error(ERROR_UPDATE_ORDER)
          }
          return updatedOrderEvent(stateObj)
        })
        ?.then((eventResp) => {
          if (!eventResp) {
            throw new Error(ERROR_CREATE_EVENT)
          }

          sendSocketData({
            messageType: NEW_IPD_DATA_UPDATE,
            patientID: uhid,
            patientLocation: '',
            message: '',
          })
          setApiLoading(false)
          setReload((r) => r + 1)
          toast(<ToastMessage message={SAMPLE_APPROVED_MSG} />, {
            position: 'bottom-center',
            hideProgressBar: true,
            closeButton: false,
            theme: 'dark',
            autoClose: 1500,
            data: { completed: true },
          })
        })
        .catch((error) => {
          console.error(error)
          toast.error(Tryagain || error?.message || GENERIC_ERROR)
        })
        .finally(() => {
          setApiLoading(false)
        })
    })
  }, [
    checkRejectAll,
    checkRejected,
    collectedTests,
    order,
    lastEvent,
    eventId,
    id,
    patient,
    uhid,
    comments,
  ])
  const formattedPatient = () => {
    const uhid = patient?.identifier?.find(
      (e: any) => e.system === 'patient-identifier'
    )?.value
    const name = makeName(patient?.name)
    const phoneNumber = patient?.telecom?.find(
      (e: any) => e.use === 'mobile'
    )?.value
    const operatingUnit = LocationByPrefix({
      id:
        patient?.identifier?.[2]?.value ??
        patient?.identifier?.[0]?.value ??
        '-',
    })

    const age = patient?.birthDate ? getAgeInYears(patient?.birthDate) : '-'
    const gender = patient?.gender
    return { uhid, name, phoneNumber, operatingUnit, age, gender }
  }
  const handleReject = useCallback(() => {
    if (
      (checkRejectAll && checkRejected.length === collectedTests.length) ||
      (!checkRejectAll && checkRejected.length === 0)
    ) {
      alert('Please select samples to reject')
      return
    }
    if (!comments.trim()) {
      alert('Comments cannot be empty while rejecting')
      return
    }
    setApiLoading(true)
    const testSet = getSelectedTestSet(
      collectedTests,
      checkRejectAll,
      checkRejected
    )
    const pastTests = lastEvent?.tests ?? []
    const current = new Date().toISOString()
    const user = KeycloakService.getUsername()
    const currentEventTests: OrderEventTest[] = [...pastTests]
    const latestOrderEvents = getLatestOrderEvents(pastTests)
    const stateObj: Partial<OrderEvent> = {
      id: eventId,
      updated_by: user,
      updated_at: current,
      tests: getCurrentEvents(
        latestOrderEvents,
        testSet,
        currentEventTests,
        COLLECTION_REJECTED
      ),
      comments: comments ?? undefined,
    }
    updatedOrderEvent(stateObj)?.then(() => {
      sendSocketData({
        messageType: NEW_IPD_DATA_UPDATE,
        patientID: uhid,
        patientLocation: '',
        message: '',
      })
      setChecked([])
      setCheckAll(false)
      setCheckRejected([])
      setCheckRejectAll(false)
      setReload((r) => r + 1)
      setApiLoading(false)
      toast(<ToastMessage message='Samples Rejected' />, {
        ...toastOptions,
        type: 'warning',
      })
      setTimeout(() => {
        // needed
        // navigate('/lms/dashboard')
      }, 1000)
    })
    const phases = getLabOrderPhases(currentEventTests)
    updateOrderPhases(id, phases)
  }, [
    checkRejectAll,
    checkRejected,
    collectedTests,
    comments,
    lastEvent,
    eventId,
    uhid,
    id,
  ])

  const LocAssignedTests = useMemo(() => {
    const eventTests: Record<number, OrderEventTest> =
      lastEvent?.tests?.reduce(
        (acc, cur) => ({
          ...acc,
          [cur.test_id]: cur,
        }),
        {}
      ) ?? {}
    return panels?.filter((p) => {
      const testId = p?.lab_tests?.[0]?.id
      return testId && eventTests?.[testId]?.status === 'LOC_ALLOCATED'
    })
  }, [panels, lastEvent])
  const patientInfo = formattedPatient()
  const labelOptions = useMemo<CLabel>(
    () => ({
      name: patientInfo?.name,
      gender: patientInfo?.gender,
      age: patientInfo?.age,
      patientId: patientInfo?.uhid,
      code: order?.lr_number ?? patientInfo?.uhid,
    }),
    [
      patientInfo?.name,
      patientInfo?.gender,
      patientInfo?.age,
      patientInfo?.uhid,
      order?.lr_number,
    ]
  )

  const handleLabelPrint = useCallback(() => {
    printLabelPDF(labelOptions)
  }, [labelOptions])

  const checkValidation = (panel: LmsPanel) => {
    const check = order?.order_events?.[0]?.tests?.find(
      (e: any) =>
        e?.test_id === panel?.lab_tests?.[0]?.id &&
        e?.status === 'COLLECTION_APPROVED'
    )
    return check?.status === 'COLLECTION_APPROVED' ? check?.updatedBy : '-'
  }
  const locationsData = useAppSelector(selectLocations)
  const locationOptions = locationsData?.map((location: any) => ({
    label: location?.resource?.name,
    value: location?.resource?.id,
  }))

  const createOrderEvent = useCallback(async () => {
    const pastTests = lastEvent?.tests ?? []
    const current = new Date().toISOString()
    const user = KeycloakService.getUsername()
    const LabOrderState = {
      labOrderId: order?.id,
      operatingUnit: order?.operating_units?.join(',') || '',
      panels:
        order?.panels?.map((panel: any) => {
          const selectedPU = selectPU?.[panel.id]?.value
          const existingPU = panel?.processing_unit

          return {
            id: panel?.id,
            processingUnit: selectedPU || existingPU || null,
          }
        }) || [],
    }

    const updatedPanels =
      order?.panels?.filter((panel: any) => selectPU?.[panel.id]?.value) || []

    const updatedTestIds: number[] =
      updatedPanels?.flatMap(
        (panel: any) => panel?.lab_tests?.map((test: any) => test.id) || []
      ) ?? []

    const currentEventTests: OrderEventTest[] = [...pastTests]

    const latestOrderEvents = getLatestOrderEvents(pastTests)

    try {
      const state = {
        id: eventId,
        updated_by: user,
        updated_at: current,
        tests: getCurrentEvents(
          latestOrderEvents,
          updatedTestIds,
          currentEventTests,
          LOC_ALLOCATED
        ),
      }
      await updatedOrderEvent(state)
      const phases = await getLabOrderPhases(currentEventTests)
      await updateOrderPhases(id, phases)
      const result = await updateLabOrderPU(LabOrderState)
      if (result?.success) {
        setShowActions(false)
        dispatch(setLmsActiveTab('LOC_ALLOCATED'))
        navigate('/lms/dashboard')
        toast.success(locErrorMsg?.AsignedLoc)
      } else {
        console.error(locErrorMsg?.FailedAsignedLoc, result.message)
        toast.error(result.message)
      }
    } catch (error) {
      console.error(error)
    }
  }, [lastEvent, order, selectPU, eventId, id, dispatch, navigate])

  const handleCancel = () => {
    setSelectedLocation(null)
    setShowActions(false)
  }
  const groupedPanels: Record<string, Panel[]> = panels.reduce(
    (acc, panel: any) => {
      const key = panel?.sampleName || null
      if (!acc[key]) acc[key] = []
      acc[key].push(panel)
      return acc
    },
    {} as Record<string, Panel[]>
  )
  const initializeSelectPU = useCallback(() => {
    if (order?.panels?.length) {
      const initialSelectPU: { [key: string]: any } = {}
      order?.panels?.forEach((panel: any) => {
        if (panel?.processing_unit) {
          initialSelectPU[panel?.id] =
            locationOptions.find(
              (opt: any) => opt?.value === panel?.processing_unit
            ) || null
        }
      })
      setSelectPU(initialSelectPU)
    }
  }, [order?.panels, locationOptions])
  useEffect(() => {
    initializeSelectPU()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <div className='collect-order'>
        <table className='data-table table-fixed admin-table relative'>
          <thead style={{ position: 'sticky', top: '0px' }}>
            <tr>
              <th className='table-w-5'>{collectOrderHeading?.SlNo}</th>
              <th>{collectOrderHeading?.LRNumber}</th>
              <th>{collectOrderHeading?.UHID}</th>
              <th>{collectOrderHeading?.Name}</th>
              <th>{collectOrderHeading?.MobileNo}</th>
              <th>{collectOrderHeading?.OP}</th>
              <th className='table-w-5'>{collectOrderHeading?.Service}</th>
              <th>{collectOrderHeading?.OrderedOn}</th>
              <th className='table-w-8'>{collectOrderHeading?.Tests}</th>
              <th>{collectOrderHeading?.Actions}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>-</td>
              <td>{patientInfo?.uhid}</td>
              <td>{patientInfo?.name}</td>
              <td>{patientInfo?.phoneNumber}</td>
              <td>{patientInfo?.operatingUnit}</td>
              <td>{order?.origin}</td>
              <td>
                {order?.ordered_on
                  ? readableDateFormat(new Date(order?.ordered_on ?? ''))
                  : '-'}
              </td>
              <td>{order?.panels?.length}</td>
              <td>
                <div className='result-actions'>
                  <button title='Print Label' onClick={handleLabelPrint}>
                    <img
                      src={LabelLogo}
                      alt='Print Label'
                      width={30}
                      height={20}
                    />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        {(!order?.id || panels.length === 0) && (
          <span className='all-collected'>
            Unable to Find Order with the given Id
          </span>
        )}
        <div style={{ marginTop: '30px' }}>
          <table className='data-table table-fixed admin-table relative'>
            <thead>
              <tr>
                <th
                  rowSpan={1}
                  className='text-center'
                  style={{ width: '5%' }}
                ></th>
                <th rowSpan={1} className='text-center'></th>
                <th rowSpan={1} className='text-center'></th>
                <th colSpan={2} className='text-center'>
                  <span style={{ marginRight: '6px' }}> Collection</span>
                  {((checkAll && checked.length < remaining) ||
                    (!checkAll && checked.length > 0)) &&
                    KeycloakService.hasRole([COLLECTION_WRITE]) && (
                      <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          justifyContent: 'center',
                        }}
                      >
                        <LoadingButton
                          title='Approve'
                          loading={apiLoading}
                          compact
                          onClick={handleCollect}
                        />
                        <LoadingButton
                          title='Reject'
                          loading={apiLoading}
                          compact
                          onClick={handleReject}
                        />
                      </div>
                    )}
                </th>
                <th className='text-center'>
                  Processing Unit
                  {showActions && (
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'center',
                      }}
                    >
                      <LoadingButton
                        title='save'
                        compact
                        onClick={createOrderEvent}
                      />
                      <LoadingButton
                        title='cancel'
                        compact
                        onClick={handleCancel}
                      />
                    </div>
                  )}
                </th>

                <th colSpan={2} className='text-center'>
                  <div className='validation-content'>
                    <span className='mt9'>Validation</span>
                    {((checkRejectAll &&
                      checkRejected.length < collectedTests.length) ||
                      (!checkRejectAll && checkRejected.length > 0)) &&
                      KeycloakService.hasRole([TESTING_WRITE]) && (
                        <div className='collect-approval package-container'>
                          <div className='approval-buttons'>
                            <LoadingButton
                              title='Approve'
                              loading={apiLoading}
                              compact
                              onClick={handleApprove}
                            />
                            <LoadingButton
                              title='Reject'
                              loading={apiLoading}
                              compact
                              onClick={handleReject}
                            />
                          </div>
                        </div>
                      )}
                  </div>
                </th>
              </tr>
              <tr>
                <th className='w4'>{collectOrderHeading?.SlNo}</th>
                <th>{LmstableHeading?.sampleName}</th>
                <th>{LmstableHeading?.TestName}</th>
                <th className='text-center'>{LmstableHeading?.CollectedBy}</th>
                <th className='text-center'>
                  Select All
                  {panels?.some((p) => !p.checked) && (
                    <span>
                      <input
                        ref={selectAllRef}
                        type='checkbox'
                        disabled={!KeycloakService.hasRole([COLLECTION_WRITE])}
                        id='select-all-samples'
                        onChange={toggleCheckAll}
                        checked={checkAll}
                      />
                    </span>
                  )}
                </th>
                <th>
                  {/* needed */}
                  {/* <input
                    type='checkbox'
                    checked={checkAll}
                    onChange={handleCheckAll}
                  />
                  {checkAll && (
                    <Select
                      value={selectedGlobalLocation}
                      isClearable
                      options={locationOptions}
                      onChange={handleGlobalSelectChange}
                      placeholder='Select Location'
                    />
                  )} */}
                </th>

                {selectedLocation != '' && (
                  <>
                    <th>
                      <div className='collection-content-select'>
                        Select All
                        {LocAssignedTests?.length > 0 && (
                          <div className='package-container'>
                            <div className='collect-tests-header'>
                              <input
                                ref={rejectAllRef}
                                type='checkbox'
                                disabled={
                                  !KeycloakService.hasRole([COLLECTION_WRITE])
                                }
                                id='reject-all-samples'
                                onChange={toggleCheckRejectAll}
                                checked={checkRejectAll}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </th>
                    <th className='text-center'>{AcknowledgeBy}</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {Object?.entries(groupedPanels)
                ?.flatMap(([, tests]) => tests)
                ?.map((e: any, i: number) => {
                  const hasCollectedTest = e?.lab_tests?.some((labTest: any) =>
                    order?.order_events?.some((event: any) =>
                      event?.tests?.some(
                        (test: any) =>
                          test?.test_id === labTest?.id &&
                          test?.status === DashboardTabs?.COLLECTED
                      )
                    )
                  )
                  const isDisabledRow = (() => {
                    if (
                      e?.processing_unit &&
                      e?.processing_unit?.trim() !== ''
                    ) {
                      const disabled = e?.processing_unit !== location
                      return disabled
                    } else if (
                      order?.operating_units &&
                      order?.operating_units?.length > 0
                    ) {
                      const allowedUnits = order?.operating_units?.map(
                        (u: string) => u?.trim()
                      )
                      const firstAllowed = allowedUnits?.[0]
                      const disabled = location !== firstAllowed
                      return disabled
                    }
                    return true
                  })()

                  return (
                    <tr
                      key={`${e?.id}-${i}`}
                      className={isDisabledRow ? 'opacity-50' : ''}
                    >
                      <td>{i + 1}</td>
                      <td>{e?.sampleName}</td>
                      <td>{e?.name}</td>
                      <td>
                        {e?.checked && e?.disabled ? e?.collectedBy : '-'}
                      </td>
                      <td className='text-center'>
                        <input
                          type='checkbox'
                          checked={
                            e?.checked || checkAll !== checked?.includes(e?.id)
                          }
                          disabled={
                            isDisabledRow ||
                            e.disabled ||
                            !KeycloakService.hasRole([COLLECTION_WRITE]) ||
                            checkAll
                          }
                          onChange={() => handleCheck(e?.id)}
                        />
                      </td>
                      <td className='text-center'>
                        {e?.processing_unit ? (
                          locationOptions?.find(
                            (opt: any) => opt?.value === e?.processing_unit
                          )?.label || '-'
                        ) : hasCollectedTest ? (
                          <Select
                            value={selectPU[e?.id] || null}
                            options={locationOptions}
                            onChange={(selected: any) => {
                              setSelectPU((prev) => {
                                const updated = { ...prev }
                                if (selected) {
                                  updated[e?.id] = selected
                                } else {
                                  delete updated[e?.id]
                                }
                                return updated
                              })
                              setShowActions(!!selected)
                            }}
                            isClearable
                            isDisabled={isDisabledRow}
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                      {selectedLocation !== '' && (
                        <>
                          <td className='text-center'>
                            {LocAssignedTests?.filter(
                              (ct) => ct?.id === e?.id
                            )?.map((ct) => (
                              <input
                                key={ct?.id}
                                type='checkbox'
                                checked={
                                  checkRejectAll !==
                                  checkRejected?.includes(ct?.id)
                                }
                                disabled={
                                  isDisabledRow ||
                                  !KeycloakService.hasRole([COLLECTION_WRITE])
                                }
                                onChange={() => handleRejectCheck(ct?.id)}
                              />
                            ))}
                          </td>
                          <td>{checkValidation(e)}</td>
                        </>
                      )}
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
        {collectedTests?.length > 0 && (
          <div className='package-container'>
            {((checkRejectAll &&
              checkRejected?.length < collectedTests?.length) ||
              (!checkRejectAll && checkRejected?.length > 0)) &&
              KeycloakService.hasRole([TESTING_WRITE]) && (
                <div className='collect-approval collect-content'>
                  <textarea
                    name='comments'
                    id='collect-comments'
                    cols={40}
                    rows={4}
                    value={comments}
                    placeholder='Comments'
                    onChange={({ target }) => setComments(target?.value)}
                  ></textarea>
                </div>
              )}
          </div>
        )}
      </div>
    </>
  )
}

export default CollectOrder
